import { __decorate } from "tslib";
import { attr, observable } from '@microsoft/fast-element';
import { FoundationElement } from '@microsoft/fast-foundation';
import { classNames } from '@microsoft/fast-web-utilities';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import localeData from 'dayjs/plugin/localeData';
import LocalizedFormat from 'dayjs/plugin/localizedFormat';
import Calendar from './calendar';
import { foundationDatePickerStyles as styles } from './date-picker.styles';
import { foundationDatePickerTemplate as template } from './date-picker.template';
import Day from './day';
export const foundationDatePickerShadowOptions = undefined;
export const defaultDatePickerConfig = {};
/**
 * @tagname %%prefix%%-date-picker
 */
export class DatePicker extends FoundationElement {
    formatChanged(oldValue, newValue) {
        if (newValue && this.calendar) {
            this.formatDate = dayjs(this.currentDate.Date).format(newValue);
        }
    }
    valueChanged(oldValue, newValue) {
        if (newValue && this.calendar) {
            const localeFormat = dayjs().localeData().longDateFormat('L');
            this.currentDate = new Day(dayjs(newValue, localeFormat).toDate(), this.lang);
            this.calendar.goToDate(this.currentDate.monthNumber, this.currentDate.year);
            this.formatDate = dayjs(this.currentDate.Date).format(this.format);
            this.updateHeaderText();
            this.monthList = this.getMonthDaysGrid();
        }
    }
    constructor() {
        super();
        this.format = 'MM-DD-YYYY';
        this.lang = 'en-US';
        this.visible = false;
        this.position = 'bottom';
        dayjs.extend(customParseFormat);
        dayjs.extend(LocalizedFormat);
        dayjs.extend(localeData);
        this.substringNumber = 3;
        const dateString = this.getAttribute('value');
        const dayjsDate = dateString ? dayjs(dateString, 'MM-DD-YYYY') : dayjs();
        this.currentDate = new Day(dayjsDate.toDate(), this.lang);
        this.calendar = new Calendar(this.currentDate.year, this.currentDate.monthNumber, this.lang);
        this.value = dayjs(this.currentDate.Date).format('MM-DD-YYYY');
    }
    connectedCallback() {
        super.connectedCallback();
        this.currentMonth = this.calendar.month.name;
        this.currentYear = this.calendar.year;
        this.formatDate = dayjs(this.currentDate.Date).format(this.format);
        this.toggleButton.addEventListener('click', () => this.toggleCalendar());
        this.prevBtn.addEventListener('click', () => this.prevMonth());
        this.nextBtn.addEventListener('click', () => this.nextMonth());
        this.monthList = this.getMonthDaysGrid();
    }
    updateHeaderText() {
        this.currentMonth = this.calendar.month.name;
        this.currentYear = this.calendar.year;
    }
    toggleCalendar() {
        this.visible = !this.visible;
        this.toggleVisibility();
        if (!this.isCurrentCalendarMonth()) {
            this.calendar.goToDate(this.currentDate.monthNumber, this.currentDate.year);
            this.updateHeaderText();
        }
    }
    toggleVisibility() {
        this.calendarDropdownClasses = classNames(['visible', this.visible], this.position);
    }
    isSelectedDate(date) {
        return dayjs(date.Date).isSame(this.currentDate.Date, 'day');
    }
    isCurrentCalendarMonth() {
        return (this.currentMonth === this.currentDate.monthNumber &&
            this.currentYear === this.currentDate.year);
    }
    prevMonth() {
        this.calendar.goToPreviousMonth();
        this.updateHeaderText();
        this.monthList = this.getMonthDaysGrid();
    }
    nextMonth() {
        this.calendar.goToNextMonth();
        this.updateHeaderText();
        this.monthList = this.getMonthDaysGrid();
    }
    selectDay(day) {
        if (dayjs(day.Date).isSame(this.currentDate.Date))
            return;
        this.currentDate = day;
        if (day.monthNumber !== this.calendar.month.number) {
            this.prevMonth();
        }
        this.formatDate = dayjs(this.currentDate.Date).format(this.format);
        this.value = dayjs(day.Date).format('MM-DD-YYYY');
        this.toggleCalendar();
    }
    getMonthDaysGrid() {
        const firstDayOfTheMonth = this.calendar.month.getDay(1);
        const prevMonth = this.calendar.getPreviousMonth();
        const totalLastMonthFinalDays = firstDayOfTheMonth.dayNumber - 1;
        const totalDays = this.calendar.month.numberOfDays + totalLastMonthFinalDays;
        const monthList = Array.from({ length: totalDays });
        for (let i = totalLastMonthFinalDays; i < totalDays; i += 1) {
            monthList[i] = this.calendar.month.getDay(i + 1 - totalLastMonthFinalDays);
        }
        for (let i = 0; i < totalLastMonthFinalDays; i += 1) {
            const inverted = totalLastMonthFinalDays - (i + 1);
            monthList[i] = prevMonth.getDay(prevMonth.numberOfDays - inverted);
        }
        return monthList;
    }
}
__decorate([
    attr
], DatePicker.prototype, "format", void 0);
__decorate([
    attr
], DatePicker.prototype, "lang", void 0);
__decorate([
    attr
], DatePicker.prototype, "visible", void 0);
__decorate([
    attr
], DatePicker.prototype, "position", void 0);
__decorate([
    attr
], DatePicker.prototype, "label", void 0);
__decorate([
    attr
], DatePicker.prototype, "value", void 0);
__decorate([
    observable
], DatePicker.prototype, "currentDate", void 0);
__decorate([
    observable
], DatePicker.prototype, "currentMonth", void 0);
__decorate([
    observable
], DatePicker.prototype, "currentYear", void 0);
__decorate([
    observable
], DatePicker.prototype, "calendarDropdownClasses", void 0);
__decorate([
    observable
], DatePicker.prototype, "formatDate", void 0);
__decorate([
    observable
], DatePicker.prototype, "monthList", void 0);
/**
 * The Foundation DatePicker
 *
 * @public
 * @remarks
 * HTML Element: \<foundation-date-picker\>
 */
export const foundationDatePicker = DatePicker.compose(Object.assign({ baseName: 'date-picker', styles,
    template, shadowOptions: foundationDatePickerShadowOptions }, defaultDatePickerConfig));
